/*
 * XEO'S TOP BAR.
 *
 * It belongs to the page instead of sitting on top of it as a banner. The bar is the
 * page's own surface (frosted, so content scrolling under a sticky child still reads),
 * closed by a hairline, with the brand carried by three small things instead of a
 * full-width blue slab:
 *   · a 2px brand strip along the very top edge
 *   · the XEO mark itself, painted in the brand gradient (the real logo's shape, through
 *     a CSS mask, so it's never redrawn)
 *   · the avatar
 *
 * The logo sits in a cell exactly as wide as the side rail under it (80 / 240, animated
 * with it) with the rail's own surface and border, so the rail and the logo read as one
 * column. Next to it, where the old bar was empty: where you are (XSite › Website
 * Builder). On the right, the one number people watch (credits) and the account,
 * one control as before.
 *
 * Colours are tokens (index.css §Top bar), so light and dark come from the same code.
 */
import { useState } from 'react';
import { Box, IconButton, Menu, MenuItem, Skeleton, Tooltip, Typography } from '@mui/material';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { Link } from 'react-router-dom';
import logoWhite from '../assets/logo_white_nobottomwords.png';
import { useTopBarPage } from '../contexts/TopBarContext';

const BAR_H = { xs: 60, md: 68 };

/** The XEO mark in the brand gradient: the logo file's shape, filled by the token. */
function XeoMark({ width }) {
  return (
    <Box role="img" aria-label="XEO" sx={{
      width, aspectRatio: '2 / 1', flexShrink: 0,
      background: 'var(--hdr-logo)',
      WebkitMaskImage: `url(${logoWhite})`, maskImage: `url(${logoWhite})`,
      WebkitMaskSize: 'contain', maskSize: 'contain',
      WebkitMaskRepeat: 'no-repeat', maskRepeat: 'no-repeat',
      WebkitMaskPosition: 'center', maskPosition: 'center',
    }} />
  );
}

/** Two letters for the avatar, from the email's name part ("dax.patel@…" → DP, "dax@…" → D). */
export function initialsOf(email) {
  const name = String(email || '').split('@')[0];
  const parts = name.split(/[._\-+]+/).filter(Boolean);
  const letters = parts.length > 1 ? parts[0][0] + parts[1][0] : name.slice(0, 1);
  return (letters || '?').toUpperCase();
}

/**
 * Titles in Title Case, everywhere in the top bar ("Site Editor", "Add Domain"): small words stay small
 * inside a title, words that already carry capitals keep them (XSite, SEO, GA4), and anything that
 * looks like an address (a domain, a URL, an email) is left exactly as it is.
 */
const SMALL_WORDS = new Set(['a', 'an', 'the', 'and', 'or', 'but', 'nor', 'of', 'to', 'in', 'on', 'for', 'with', 'by', 'at', 'as', 'vs', 'via', 'from', 'per']);
export function titleCase(s) {
  if (typeof s !== 'string' || /[./@:]/.test(s) || !/[a-z]/.test(s)) return s;
  const parts = s.split(/(\s+)/);
  return parts.map((w, i) => {
    if (!w || /^\s+$/.test(w)) return w;
    if (/[A-Z]/.test(w.slice(1))) return w;
    const lower = w.toLowerCase();
    if (i > 0 && i < parts.length - 1 && SMALL_WORDS.has(lower)) return lower;
    return w.charAt(0).toUpperCase() + w.slice(1);
  }).join('');
}

/**
 * The path under the title: the sidebar's section (its pages), the sidebar item (its views),
 * the view open now (?view=, its siblings), then whatever the page adds (a domain, its tab).
 */
export function pathSteps(nav, page) {
  if (!nav) return [];
  const steps = [{ label: nav.section.title, menu: nav.section.items }];
  const pageTrail = page?.trail || [];
  const here = page?.title || (nav.deeper ? nav.deeperTitle : '');
  const deeper = !!nav.view || pageTrail.length > 0 || (nav.deeper && here && here !== nav.item.label);
  steps.push(deeper ? { label: nav.item.label, onClick: nav.item.onClick, menu: nav.item.menu } : { label: nav.item.label, menu: nav.item.menu });
  if (nav.view) steps.push({ label: nav.view.label, menu: nav.view.menu });
  if (pageTrail.length) steps.push(...pageTrail);
  else if (nav.deeper && here && here !== nav.item.label && !nav.view) steps.push({ label: here });
  return steps;
}

/** One step of the path: a link, a menu, or (the last one) where you are. */
function Crumb({ step, current }) {
  const [anchor, setAnchor] = useState(null);
  const hasMenu = Array.isArray(step.menu) && step.menu.length > 0;
  const clickable = hasMenu || !!step.onClick;
  const open = (e) => { if (hasMenu) setAnchor(e.currentTarget); else if (step.onClick) step.onClick(); };
  return (
    <>
      <Box component={clickable ? 'button' : 'span'} type={clickable ? 'button' : undefined} onClick={clickable ? open : undefined}
        aria-haspopup={hasMenu ? 'menu' : undefined} aria-expanded={hasMenu ? !!anchor : undefined} aria-current={current ? 'page' : undefined}
        sx={{
          display: 'inline-flex', alignItems: 'center', gap: '1px', minWidth: 0, border: 0, background: 'none', font: 'inherit',
          p: '2px 6px', borderRadius: '7px', cursor: clickable ? 'pointer' : 'default',
          fontSize: 12.75, lineHeight: 1.4, whiteSpace: 'nowrap', letterSpacing: '0.005em',
          fontWeight: current ? 700 : 600, color: current ? 'var(--ink-2)' : 'var(--ink-3)',
          transition: 'background-color .14s ease, color .14s ease',
          '&:hover': clickable ? { color: 'var(--accent)', backgroundColor: 'var(--tint)' } : {},
          '&:focus-visible': { outline: '2px solid var(--accent)', outlineOffset: 1 },
        }}>
        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }}>{titleCase(step.label)}</Box>
        {hasMenu && <KeyboardArrowDownRoundedIcon aria-hidden sx={{ fontSize: 15, opacity: 0.8 }} />}
      </Box>
      {hasMenu && (
        <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{ paper: { sx: { mt: 0.75, minWidth: 200, borderRadius: '12px', border: '1px solid var(--line)', boxShadow: 'var(--hdr-pop)' } } }}>
          {step.menu.map((m, i) => (
            <MenuItem key={i} selected={!!m.active} onClick={() => { setAnchor(null); m.onClick && m.onClick(); }}
              sx={{ fontSize: 14, fontWeight: m.active ? 700 : 500, borderRadius: '8px', mx: 0.75, my: 0.25, gap: 1.25 }}>
              {m.icon && <Box component="span" sx={{ display: 'inline-flex', '& svg': { fontSize: 18 } }}>{m.icon}</Box>}
              <Box component="span" sx={{ flex: 1 }}>{m.label}</Box>
              {m.active && <CheckRoundedIcon sx={{ fontSize: 17, color: 'var(--accent)' }} />}
            </MenuItem>
          ))}
        </Menu>
      )}
    </>
  );
}

const ghostButton = {
  width: 40, height: 40, borderRadius: '12px', color: 'var(--ink-2)',
  border: '1px solid var(--line)', backgroundColor: 'var(--surface)',
  transition: 'background-color .16s ease, border-color .16s ease, transform .16s ease',
  '&:hover': { backgroundColor: 'var(--tint)', borderColor: 'var(--hdr-line-strong)' },
  '&:focus-visible': { outline: '2px solid var(--accent)', outlineOffset: 2 },
};

export default function AppTopBar({
  isMobile, isSmallScreen, collapsed, nav, colorMode,
  planLoaded, creditsError, credits, email, onOpenMenu, onAccount,
}) {
  const dark = colorMode.resolved === 'dark';
  const page = useTopBarPage();
  const title = titleCase(page?.title || (nav?.deeper && nav?.deeperTitle) || nav?.item?.label || '');
  const subtitle = page?.subtitle || '';
  const steps = pathSteps(nav, page);
  const balance = !planLoaded
    ? <Skeleton variant="text" width={34} sx={{ display: 'inline-block', bgcolor: 'var(--wash)' }} />
    : creditsError ? '—' : (Math.round((Number(credits) || 0) * 100) / 100).toLocaleString('en-US', { maximumFractionDigits: 2 });

  return (
    <Box component="header" data-xeo-topbar="" sx={{
      position: 'relative', zIndex: 2, flexShrink: 0,
      display: 'flex', alignItems: 'stretch', height: BAR_H,
      backgroundColor: 'var(--hdr-bg)',
      backdropFilter: 'saturate(1.6) blur(14px)', WebkitBackdropFilter: 'saturate(1.6) blur(14px)',
      borderBottom: '1px solid var(--line)',
      boxShadow: 'var(--hdr-shadow)',
      // The brand strip: 2px along the very top.
      '&::before': {
        content: '""', position: 'absolute', left: 0, right: 0, top: 0, height: '2px',
        background: 'var(--hdr-strip)', pointerEvents: 'none',
      },
    }}>
      {/* The logo cell: the rail's width, surface and border, so the two read as one column. */}
      {isMobile ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pl: 1, pr: 1.5 }}>
          <Tooltip title="Open menu" arrow enterDelay={0}>
            <IconButton onClick={onOpenMenu} aria-label="Open menu" sx={{ color: 'var(--ink)' }}>
              <MenuIcon />
            </IconButton>
          </Tooltip>
          <Box component={Link} to="/dashboard/domains" aria-label="XEO home" sx={{ display: 'inline-flex', '&:focus-visible': { outline: '2px solid var(--accent)', outlineOffset: 3, borderRadius: 1 } }}>
            <XeoMark width={54} />
          </Box>
        </Box>
      ) : (
        <Box sx={{
          width: collapsed ? 80 : 240, flexShrink: 0, transition: 'width 0.3s ease',
          display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start',
          px: collapsed ? 0 : 3,
          backgroundColor: 'var(--surface)', borderRight: '1px solid var(--line)',
        }}>
          <Box component={Link} to="/dashboard/domains" aria-label="XEO home" sx={{
            display: 'inline-flex', alignItems: 'center', gap: 1.25, textDecoration: 'none', borderRadius: '10px', p: 0.5,
            transition: 'transform .18s ease', '&:hover': { transform: 'translateY(-1px)' },
            '&:focus-visible': { outline: '2px solid var(--accent)', outlineOffset: 2 },
          }}>
            <XeoMark width={collapsed ? 58 : 66} />
          </Box>
        </Box>
      )}

      {/* Where you are: the page's title and subtitle, and the path to it (each step opens its menu). */}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: { xs: 1.25, md: 1.75 }, px: { xs: 0.5, md: 3 } }}>
        {page?.back && (
          <Tooltip title={page.back.label} arrow>
            <IconButton onClick={page.back.onClick} aria-label={page.back.label} sx={{ ...ghostButton, width: 34, height: 34, borderRadius: '10px' }}>
              <ArrowBackIosNewRoundedIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        )}
        {nav?.item?.icon && !isSmallScreen && (
          <Box aria-hidden sx={{
            width: 40, height: 40, borderRadius: '12px', flexShrink: 0,
            display: 'grid', placeItems: 'center', backgroundColor: 'var(--tint)',
            border: '1px solid var(--hdr-line-soft)', '& svg': { fontSize: 20 },
          }}>{nav.item.icon}</Box>
        )}
        {title && (
          <Box sx={{ minWidth: 0, display: 'grid', gap: '4px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
              <Typography component="h1" data-testid="topbar-title" sx={{
                fontSize: { xs: 15.5, md: 17 }, fontWeight: 700, color: 'var(--ink)', letterSpacing: '-0.018em', lineHeight: 1.3,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: { xs: '46vw', md: 420 },
              }}>{title}</Typography>
              {subtitle && !isMobile && (
                <Typography component="span" data-testid="topbar-subtitle" sx={{
                  fontSize: 13.5, fontWeight: 500, color: 'var(--ink-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0,
                  lineHeight: 1.3, pl: 1.25, borderLeft: '1px solid var(--line)',
                }}>{subtitle}</Typography>
              )}
            </Box>
            {steps.length > 0 && !isSmallScreen && (
              <Box component="nav" aria-label="You are here" data-testid="topbar-crumb" sx={{ display: 'flex', alignItems: 'center', gap: '2px', minWidth: 0, overflow: 'hidden', ml: '-6px' }}>
                {steps.map((step, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0 }}>
                    {i > 0 && <ChevronRightRoundedIcon aria-hidden sx={{ fontSize: 14, color: 'var(--ink-4)', mx: '1px' }} />}
                    <Crumb step={step} current={i === steps.length - 1} />
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* The theme, then credits + account (one control, as before). */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, pr: { xs: 1.5, md: 3 } }}>
        <Tooltip arrow title={dark ? 'Switch to light' : 'Switch to dark'}>
          <IconButton onClick={colorMode.toggle} aria-label={dark ? 'Switch to light theme' : 'Switch to dark theme'} sx={ghostButton}>
            {dark ? <LightModeRoundedIcon sx={{ fontSize: 19 }} /> : <DarkModeRoundedIcon sx={{ fontSize: 19 }} />}
          </IconButton>
        </Tooltip>
        <Box
          onClick={onAccount}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onAccount(e); } }}
          aria-label={creditsError ? 'Account and credits (balance unavailable)' : 'Account and credits'}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1, height: 42,
            pl: { xs: 1, sm: 1.25 }, pr: 0.5, borderRadius: 999, cursor: 'pointer',
            border: '1px solid var(--line)', backgroundColor: 'var(--surface)',
            transition: 'border-color .16s ease, box-shadow .16s ease, transform .16s ease',
            '&:hover': { borderColor: 'var(--hdr-line-strong)', boxShadow: 'var(--hdr-pop)', transform: 'translateY(-1px)' },
            '&:focus-visible': { outline: '2px solid var(--accent)', outlineOffset: 2 },
          }}
        >
          {/* The balance: a warm chip, the one place a second hue is earned. */}
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 0.5, height: 28, px: 1, borderRadius: 999,
            backgroundColor: 'var(--hdr-credit-bg)', border: '1px solid var(--hdr-credit-line)',
          }}>
            <LocalFireDepartmentIcon sx={{ fontSize: 16, color: 'var(--hdr-flame)' }} />
            <Typography component="span" sx={{ fontSize: 14, fontWeight: 800, color: 'var(--hdr-credit-fg)', fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
              {balance}
            </Typography>
            {!isSmallScreen && (
              <Typography component="span" sx={{ fontSize: 12, fontWeight: 600, color: 'var(--hdr-credit-fg)', opacity: 0.75, lineHeight: 1 }}>
                credits
              </Typography>
            )}
          </Box>
          <Box aria-hidden sx={{
            width: 30, height: 30, borderRadius: '50%', display: 'grid', placeItems: 'center',
            background: 'var(--hdr-avatar)', color: '#fff', fontSize: 12.5, fontWeight: 800, letterSpacing: '0.02em',
            boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.25)',
          }}>
            {initialsOf(email)}
          </Box>
          {!isSmallScreen && <KeyboardArrowDownRoundedIcon aria-hidden sx={{ fontSize: 18, color: 'var(--ink-3)', mr: 0.25 }} />}
        </Box>
      </Box>
    </Box>
  );
}
